import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import ProposalTermsEditor from '../../components/ProposalTermsEditor';
import { UserCheck, Clock, CheckCircle, XCircle, Mail, Phone, Building, MapPin, Send } from 'lucide-react';

// The applicant's own answers, in readable form. These mirror the vocabularies in
// backend/server.py and the two application forms — same rule as everywhere else: the
// options and their labels are one artefact in several places, edited together.
const BUSINESS_TYPES = {
  agency: 'An agency',
  tech_partner: 'A technology partner',
  freelancer: 'An automation freelancer',
  consultant: 'A consultant',
};

const CLIENT_COUNTS = {
  '20+': '20 or more', '10-19': '10 to 19', '5-9': '5 to 9',
  '3-4': '3 or 4', '1-2': '1 or 2', '0': 'None yet',
};

const RUNS_ON = {
  crm: 'a CRM', workflow_builder: 'a workflow builder', marketing_platform: 'a marketing platform',
  own_platform: 'their own platform', sheets: 'Sheets or Excel', nothing: 'nothing yet', other: 'something else',
};

const RETAINER = {
  gt75: 'More than 75%', '50-75': '50 to 75%', '25-49': '25 to 49%', lt25: 'Under 25%',
};

const DELIVERY = {
  platform_team: 'A dedicated ops team', campaign_ops: 'Campaign and ad ops, no platform',
  founder_led: 'Founder-operated', outsourced: 'All delivery outsourced',
};

const POSTURE = {
  resells: 'They resell or mark up licences', bundles: 'Bundled into the retainer',
  client_direct: 'Client buys direct, they operate', hours_only: 'They bill hours only',
};

/** One answer. Renders nothing when unanswered rather than showing an empty row, so an
 *  application still being filled in does not read as a wall of blanks. */
const Fact = ({ label, value, strong }) => {
  if (!value) return null;
  return (
    <div className="text-sm">
      <span className="text-[#64748B]">{label}: </span>
      <span className={strong ? 'font-semibold text-[#1A1A1A]' : 'text-[#1A1A1A]'}>{value}</span>
    </div>
  );
};

const AdminApprovals = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [pendingPartners, setPendingPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  // Approve, then immediately offer the proposal — see handleApproval.
  const [proposalTarget, setProposalTarget] = useState(null);
  const [termsDiff, setTermsDiff] = useState({});
  const [sending, setSending] = useState(false);
  const [sentProposal, setSentProposal] = useState(null);

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/pending-approvals`, {
        headers: getAuthHeaders()
      });
      setPendingPartners(response.data);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (partnerId, action) => {
    setProcessingId(partnerId);
    try {
      const status = action === 'approve' ? 'active' : 'inactive';
      await axios.put(`${API_URL}/api/admin/partners/${partnerId}/status?status=${status}`, null, {
        headers: getAuthHeaders()
      });
      toast.success(action === 'approve' ? 'Partner approved successfully!' : 'Partner application rejected');
      // Approving is never the last step — a proposal follows it every time, and this is the
      // one moment the terms are being decided. Offering it here rather than sending them off
      // to the Partners screen is the difference between editing the numbers and forgetting to.
      if (action === 'approve') {
        const approved = pendingPartners.find((p) => p.id === partnerId);
        setProposalTarget(approved || { id: partnerId, full_name: 'this partner' });
        setTermsDiff({});
        setSentProposal(null);
      }
      await fetchPendingApprovals();
    } catch (error) {
      toast.error('Failed to process application');
    } finally {
      setProcessingId(null);
    }
  };

  const sendProposal = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/proposals`, {
        partner_id: proposalTarget.id,
        send: true,
        terms_diff: Object.keys(termsDiff).length ? termsDiff : null,
      }, { headers: getAuthHeaders() });
      setSentProposal(res.data);
      toast.success('Proposal sent.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not send that proposal');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-approvals">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">
              Partner Approvals
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              Review and approve new partner applications
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-lg border border-amber-200">
            <Clock className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-amber-700" data-testid="pending-count">{pendingPartners.length}</span>
            <span className="text-amber-600 text-sm">pending</span>
          </div>
        </div>

        {/* Pending Applications */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ef785b]"></div>
          </div>
        ) : pendingPartners.length === 0 ? (
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-12 text-center">
              <UserCheck className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
                All Caught Up!
              </h3>
              <p className="text-[#64748B]">
                There are no pending partner applications to review.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {pendingPartners.map((partner, index) => (
              <Card key={partner.id} className="bg-white border-[#E2E2DF]" data-testid={`approval-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    {/* Partner Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-14 h-14 rounded-full bg-[#ef785b]/10 flex items-center justify-center">
                          <span className="text-xl font-bold text-[#ef785b]">
                            {partner.full_name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#1A1A1A]">
                            {partner.full_name}
                          </h3>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#ef785b]/10 text-[#ef785b] capitalize">
                              {partner.tier || 'silver'} Partner
                            </span>
                            <span className="text-xs text-[#64748B]">
                              Applied {formatDate(partner.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-[#64748B]" />
                          <span className="text-[#1A1A1A]">{partner.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-[#64748B]" />
                          <span className="text-[#1A1A1A]">{partner.mobile}</span>
                        </div>
                        {/* City and state are asked in the second sitting now, so this is
                            empty for anyone who has not finished. Rendering the separator
                            regardless left a bare "," sitting in the card. */}
                        {(partner.city || partner.state) && (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-[#64748B]" />
                            <span className="text-[#1A1A1A]">
                              {[partner.city, partner.state].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}
                        {partner.company_name && (
                          <div className="flex items-center gap-2 text-sm">
                            <Building className="w-4 h-4 text-[#64748B]" />
                            <span className="text-[#1A1A1A]">{partner.company_name}</span>
                          </div>
                        )}
                      </div>

                      {/* What the applicant actually told us. There is no grade any more
                          (backend/scorecard.py is parked), so this IS the basis on which
                          somebody decides — facts, led by client count, rather than a
                          letter computed from weights nobody has calibrated. */}
                      <div className="mt-4 grid gap-x-6 gap-y-2 rounded-lg bg-[#FAFAF8] p-3 sm:grid-cols-2">
                        <Fact label="Clients" value={CLIENT_COUNTS[partner.client_count]} strong />
                        <Fact label="They are" value={BUSINESS_TYPES[partner.business_type]} />
                        <Fact label="Runs on today" value={(partner.runs_on || []).map((r) => RUNS_ON[r] || r).join(', ')} />
                        <Fact label="Team size" value={partner.team_size} />
                        <Fact label="On retainer" value={RETAINER[partner.retainer_share]} />
                        <Fact label="Who operates" value={DELIVERY[partner.delivery_capability]} />
                        <Fact label="Software reaches clients" value={POSTURE[partner.commercial_posture]} />
                        <Fact label="Industries" value={(partner.verticals || []).join(', ')} />
                        <Fact label="Work profile" value={partner.work_proof} />
                      </div>

                      {/* Derived from what they run client work on, rather than from a
                          separate tick-box asking the same thing a step later. Deliberately
                          worded as a question: running your own platform is not the same as
                          reselling one, and only the call can tell those apart. */}
                      {(partner.runs_on || []).includes('own_platform') && (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                          <p className="text-sm font-semibold text-amber-800">
                            Runs client work on a platform they built themselves
                          </p>
                          <p className="mt-0.5 text-sm text-amber-700">
                            Worth asking on the call whether they sell it to clients. If they do, they
                            are a competitor rather than a partner.
                          </p>
                        </div>
                      )}
                      {partner.builds_competing_platform && (
                        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                          <p className="text-sm font-semibold text-red-800">
                            Confirmed: builds or resells a competing platform
                          </p>
                        </div>
                      )}

                      {partner.application_complete === false && (
                        <p className="mt-3 text-sm text-amber-700">
                          Application not finished yet, so most of the above is still blank. They have
                          been emailed a link.
                        </p>
                      )}

                      {/* Nothing is deduped at the door any more: a second applicant from a
                          domain we already have is accepted and flagged here, because this
                          is the screen where the irreversible decision gets made. Approving
                          two of these puts two commission streams on one book, and no
                          amount of cleanup afterwards separates them again. */}
                      {partner.domain_siblings?.length > 0 && (
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">
                            Already have {partner.domain_siblings.length} partner
                            {partner.domain_siblings.length > 1 ? 's' : ''} on this domain
                          </p>
                          <p className="text-sm text-amber-900">
                            Approve only one of these, or the agency ends up with two partner records
                            and two commission streams on one book.
                          </p>
                          <ul className="mt-2 space-y-1">
                            {partner.domain_siblings.map((s) => (
                              <li key={s.id} className="text-sm text-amber-900">
                                {s.full_name || s.email}
                                {s.company_name ? ` · ${s.company_name}` : ''}
                                {s.status ? ` · ${s.status}` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {partner.network_description && (
                        <div className="mt-4 p-3 bg-[#FAFAF8] rounded-lg">
                          <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Network Description</p>
                          <p className="text-sm text-[#1A1A1A]">{partner.network_description}</p>
                        </div>
                      )}

                      {partner.linkedin_url && (
                        <a 
                          href={partner.linkedin_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-3 text-sm text-[#ef785b] hover:underline"
                        >
                          View LinkedIn Profile →
                        </a>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex lg:flex-col gap-3">
                      <Button
                        onClick={() => handleApproval(partner.id, 'approve')}
                        disabled={processingId === partner.id}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`approve-${index}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {processingId === partner.id ? 'Processing...' : 'Approve'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleApproval(partner.id, 'reject')}
                        disabled={processingId === partner.id}
                        className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`reject-${index}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {/* Approve, then set the terms. One flow, because in practice they are one decision. */}
        <Dialog open={!!proposalTarget}
                onOpenChange={() => { setProposalTarget(null); setTermsDiff({}); setSentProposal(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {sentProposal ? 'Proposal sent' : `Send a proposal to ${proposalTarget?.full_name || ''}`}
              </DialogTitle>
            </DialogHeader>

            {!sentProposal ? (
              <div className="space-y-4">
                <p className="text-sm text-[#64748B]">
                  Approved. Now the terms. Accepting <strong className="text-[#1A1A1A]">freezes
                  these numbers to them</strong> — a later change to the published card will not
                  move them, and re-negotiating means sending another proposal.
                </p>
                <ProposalTermsEditor partnerId={proposalTarget?.id} onChange={setTermsDiff} />
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-medium text-[#1A1A1A]">Their link:</p>
                <div className="bg-[#FAFAF8] border border-[#E2E2DF] rounded-md p-3 break-all text-xs font-mono">
                  {sentProposal.url}
                </div>
                <p className="text-sm text-[#64748B]">
                  {Object.keys(sentProposal.changed || {}).length
                    ? `${Object.keys(sentProposal.changed).length} negotiated change(s) on it.`
                    : 'Standard terms, unchanged.'}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" className="border-[#E2E2DF]"
                      onClick={() => { setProposalTarget(null); setTermsDiff({}); setSentProposal(null); }}>
                {sentProposal ? 'Done' : 'Skip for now'}
              </Button>
              {!sentProposal && (
                <Button onClick={sendProposal} disabled={sending}
                        className="bg-[#ef785b] hover:bg-[#D44017] text-white"
                        data-testid="send-proposal-on-approve">
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Sending…' : 'Send proposal'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminApprovals;
